// POROS — Resource Readiness (the REGULATE deity's tool).
// Answers one question: "Do I have everything I need to accomplish this objective?"
// Six fixed categories × any number of individually checkable resources. A
// category can be toggled off (Not needed): it leaves the readiness count but
// keeps its entries. A check means the USER confirmed availability — AI
// suggestions always start unchecked.
//
// Store: user_settings.oracle_spine[sector].atlas.poros[key], key = stage +
// objective, so the same objective reuses its saved list (read-modify-write).
import type { SupabaseClient } from '@supabase/supabase-js';
import { SECTORS, type Sector } from '../domain/olmps';
import { emptyAtlasData, type StageKey } from './atlas-data';
import { initialOracleSpine } from './oracle-spine-data';

export type PorosCat = 'people' | 'places' | 'products' | 'processes' | 'prerequisites' | 'parameters';
export const POROS_CATS: { key: PorosCat; label: string }[] = [
  { key: 'people', label: 'People' },
  { key: 'places', label: 'Places' },
  { key: 'products', label: 'Products' },
  { key: 'processes', label: 'Processes' },
  { key: 'prerequisites', label: 'Prerequisites' },
  { key: 'parameters', label: 'Parameters' },
];
export const POROS_QUESTION = 'Do I have everything I need to accomplish this objective?';

export interface PorosItem {
  id: string;
  text: string;
  checked: boolean;
  /** Suggested by POROS/SyQi (not yet confirmed by the user). */
  ai?: boolean;
}
export interface PorosRecord {
  objective: string;
  cats: Record<PorosCat, { on: boolean; items: PorosItem[] }>;
  /** The user's own answer to "Do I have everything I need?" (overrides the generated one). */
  said?: string;
  /** The user's own answer to "What resources do I need?" (overrides the generated one). */
  needSaid?: string;
  updatedAt?: string;
}

export function emptyPoros(objective: string): PorosRecord {
  const cats = {} as PorosRecord['cats'];
  for (const c of POROS_CATS) cats[c.key] = { on: true, items: [] };
  return { objective, cats };
}

const slug = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
export const porosKey = (stage: StageKey, objective: string) => `${stage}:${slug(objective)}`;
export const newItemId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Read a record out of an (untyped) spine node. */
export function porosOf(node: unknown, key: string): PorosRecord | null {
  const rec = (node as { atlas?: { poros?: Record<string, PorosRecord> } } | null | undefined)?.atlas?.poros?.[key];
  if (!rec) return null;
  const base = emptyPoros(rec.objective ?? '');
  for (const c of POROS_CATS) {
    const r = rec.cats?.[c.key];
    if (r) base.cats[c.key] = { on: r.on !== false, items: Array.isArray(r.items) ? r.items : [] };
  }
  return { ...base, said: rec.said, needSaid: rec.needSaid, updatedAt: rec.updatedAt };
}

export async function loadPoros(supabase: SupabaseClient, userId: string, sector: Sector, key: string): Promise<PorosRecord | null> {
  const { data, error } = await supabase.from('user_settings').select('oracle_spine').eq('user_id', userId).maybeSingle();
  if (error) throw new Error(error.message);
  const spine = (data as { oracle_spine?: Record<string, unknown> } | null)?.oracle_spine ?? {};
  return porosOf(spine[sector], key);
}

/** Write one record into the sector's node; nothing else is touched. */
export async function savePoros(supabase: SupabaseClient, userId: string, sector: Sector, key: string, rec: PorosRecord): Promise<void> {
  const { data, error } = await supabase.from('user_settings').select('oracle_spine').eq('user_id', userId).maybeSingle();
  if (error) throw new Error(error.message);
  const spine = ((data as { oracle_spine?: Record<string, unknown> } | null)?.oracle_spine ?? {}) as Record<string, Record<string, unknown>>;
  const node = { ...(spine[sector] ?? initialOracleSpine()[sector]) } as Record<string, unknown>;
  const atlas = { ...((node.atlas as Record<string, unknown> | undefined) ?? emptyAtlasData()) } as Record<string, unknown>;
  atlas.poros = { ...((atlas.poros as Record<string, PorosRecord> | undefined) ?? {}), [key]: { ...rec, updatedAt: new Date().toISOString() } };
  node.atlas = atlas;
  const next = { ...spine, [sector]: node };
  for (const s of SECTORS) if (!next[s]) next[s] = initialOracleSpine()[s] as unknown as Record<string, unknown>;
  const { error: upErr } = await supabase
    .from('user_settings')
    .upsert({ user_id: userId, oracle_spine: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (upErr) throw new Error(upErr.message);
}

export interface Readiness {
  ready: number;
  total: number;
  missing: PorosItem[];
}
/** Only enabled categories count. */
export function readiness(rec: PorosRecord | null): Readiness {
  const out: Readiness = { ready: 0, total: 0, missing: [] };
  if (!rec) return out;
  for (const c of POROS_CATS) {
    const cat = rec.cats[c.key];
    if (!cat.on) continue;
    for (const it of cat.items) {
      if (!it.text.trim()) continue;
      out.total += 1;
      if (it.checked) out.ready += 1;
      else out.missing.push(it);
    }
  }
  return out;
}

const lowerFirst = (x: string) => (x.length > 1 && x[1] === x[1].toLowerCase() ? x.charAt(0).toLowerCase() + x.slice(1) : x);
const joinList = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** The Regulate answer as one complete sentence, from the table. Empty when nothing is listed. */
export function readinessSentence(rec: PorosRecord | null): string {
  const r = readiness(rec);
  if (r.total === 0) return '';
  if (r.missing.length === 0) return 'I have everything I need.';
  if (r.ready === 0) return `I still need ${joinList(r.missing.map((m) => lowerFirst(m.text.trim())))}.`;
  return `I have everything except ${joinList(r.missing.map((m) => lowerFirst(m.text.trim())))}.`;
}

/** Merge AI suggestions: add new (unchecked, ai) items, turn on categories that got items. */
export function mergeSuggestions(rec: PorosRecord, sug: Partial<Record<PorosCat, string[]>>): PorosRecord {
  const next: PorosRecord = { ...rec, cats: { ...rec.cats } };
  for (const c of POROS_CATS) {
    const list = (sug[c.key] ?? []).map((x) => String(x).trim()).filter(Boolean);
    const cur = next.cats[c.key];
    const have = new Set(cur.items.map((i) => i.text.trim().toLowerCase()));
    const add = list.filter((x) => !have.has(x.toLowerCase())).map((text) => ({ id: newItemId(), text, checked: false, ai: true }));
    const on = cur.items.length + add.length > 0 ? true : sug[c.key] !== undefined ? false : cur.on;
    next.cats[c.key] = { on, items: [...cur.items, ...add] };
  }
  return next;
}

/** The prompt POROS sends to propose typical resources for an objective. */
export function suggestPrompt(objective: string): string {
  return [
    'You are POROS, the resource-readiness deity. List the typical resources someone needs to accomplish this objective:',
    `"${objective}"`,
    'Reply with ONLY a JSON object, no prose, with exactly these keys, each an array of short item names (max 6 each, [] when the category is not needed):',
    '{"people":[],"places":[],"products":[],"processes":[],"prerequisites":[],"parameters":[]}',
    'People = who is needed. Places = where. Products = physical or digital things. Processes = procedures or methods.',
    'Prerequisites = conditions that must already be satisfied. Parameters = measurable limits: time, budget, quantities, temperatures, deadlines.',
  ].join('\n');
}

/** Pull the JSON object out of a model reply. */
export function parseSuggestions(text: string): Partial<Record<PorosCat, string[]>> | null {
  const m = text.match(/\{[\s\S]*\}/);
  if (!m) return null;
  try {
    const raw = JSON.parse(m[0]) as Record<string, unknown>;
    const out: Partial<Record<PorosCat, string[]>> = {};
    for (const c of POROS_CATS) {
      const v = raw[c.key];
      if (Array.isArray(v)) out[c.key] = v.filter((x): x is string => typeof x === 'string').slice(0, 8);
    }
    return out;
  } catch {
    return null;
  }
}

// Where the POROS tool should open: the orb sets this right before navigating.
export interface PorosTarget {
  sector: Sector;
  stage: StageKey;
  objective: string;
}
let target: PorosTarget | null = null;
export const setPorosTarget = (t: PorosTarget | null) => {
  target = t;
};
export const getPorosTarget = () => target;

// Live cache so the orb's Regulate row reflects table edits immediately.
const cache = new Map<string, PorosRecord>();
const listeners = new Set<() => void>();
export const porosCacheKey = (sector: Sector, key: string) => `${sector}|${key}`;
export function cachePoros(sector: Sector, key: string, rec: PorosRecord) {
  cache.set(porosCacheKey(sector, key), rec);
  listeners.forEach((fn) => fn());
}
export const cachedPoros = (sector: Sector, key: string) => cache.get(porosCacheKey(sector, key)) ?? null;
export function subscribePoros(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** "What resources do I need?" as one sentence: every listed resource in the enabled categories. */
export function needsSentence(rec: PorosRecord | null): string {
  if (!rec) return '';
  const items: string[] = [];
  for (const c of POROS_CATS) {
    const cat = rec.cats[c.key];
    if (!cat.on) continue;
    for (const it of cat.items) if (it.text.trim()) items.push(lowerFirst(it.text.trim()));
  }
  if (!items.length) return '';
  const shown = items.slice(0, 8);
  return `I need ${joinList(shown)}${items.length > shown.length ? `, plus ${items.length - shown.length} more` : ''}.`;
}
